import type { DateTime } from 'luxon';
// eslint-disable-next-line @nx/enforce-module-boundaries -- o mesmo ciclo settings → shared-utils que o edge-control.service.ts já tinha; o getDateTime só mudou de arquivo com a regra
import { getDateTime } from '@shared/utils';
import { IEdgeEventPayload } from './access-edges.interface';

/**
 * Tentativa bloqueada: a leitura de uma credencial, ou a liberação manual de uma
 * pessoa específica (`ManualTargeted`), que a avaliação de acesso recusou. As
 * duas passam pela avaliação e, quando negadas, trazem `AttemptId` e
 * `AttemptTtlSeconds` — é sobre elas que a liberação sobre recusa age.
 *
 * O sublinhado da frase na Live Access Activity, o botão de liberar e o som da
 * recusa no mini-monitor destacado decidem todos por aqui, para não discordarem
 * sobre quais recusas ainda se podem liberar.
 */
export function isBlockedAttemptPayload(
  payload:
    | Pick<IEdgeEventPayload, 'ControlOutcome' | 'TriggerKind'>
    | null
    | undefined
): boolean {
  const triggerKind = payload?.TriggerKind;
  return (
    payload?.ControlOutcome === 'Blocked' &&
    (triggerKind === 'AutomaticCredential' || triggerKind === 'ManualTargeted')
  );
}

/**
 * O prazo em que uma recusa ainda se pode liberar: quantos segundos o servidor
 * lhe concedeu e quando ela aconteceu.
 */
export interface IRefusalReleaseWindow {
  /** O `AttemptTtlSeconds` da recusa. */
  ttlSeconds: number;
  /** Instante da recusa, em milissegundos, na hora da conta — como o `DateTime.now()`. */
  occurredAtMillis: number;
}

/**
 * O prazo para liberar uma recusa, ou `null` quando não há prazo a correr: não é
 * recusa (`Allowed` não é `false`), não traz `AttemptTtlSeconds` positivo, ou não
 * tem instante legível. Sem prazo quer dizer "não dá para liberar", nunca "dá para
 * sempre": a Live Access Activity carrega histórico de dias anteriores, e um botão
 * sem prazo ali é um 404 à espera de acontecer.
 *
 * Só o prazo decide aqui. Se a recusa é uma tentativa bloqueada com `AttemptId`
 * fica com quem chama, e os dois consumidores não o fazem no mesmo sítio: o item
 * da Live Access Activity conta o prazo de qualquer recusa e exige o resto no
 * template do botão; o som do mini-monitor destacado exige tudo junto.
 *
 * `occurredOn` chega já lido pelo `getDateTime`: o hub converte o `occurredOnUtc`
 * para a hora da conta na entrada, e reconverter deslocaria o prazo.
 */
export function getRefusalReleaseWindow(
  payload: Pick<IEdgeEventPayload, 'Allowed' | 'AttemptTtlSeconds'> | null | undefined,
  occurredOn: DateTime | null | undefined
): IRefusalReleaseWindow | null {
  const ttlSeconds = payload?.AttemptTtlSeconds;
  if (
    payload?.Allowed !== false ||
    typeof ttlSeconds !== 'number' ||
    ttlSeconds <= 0 ||
    !occurredOn?.isValid
  ) {
    return null;
  }
  return { ttlSeconds, occurredAtMillis: occurredOn.toMillis() };
}

/**
 * Segundos inteiros que ainda restam para liberar a recusa; `0` quando o prazo
 * acabou. É o número do botão "Liberar (Ns)", e o botão só existe acima de zero —
 * por isso "ainda se pode liberar agora" é este valor maior que zero, também para
 * o som: com menos de um segundo o botão já saiu.
 */
export function getRefusalReleaseRemainingSeconds(
  releaseWindow: IRefusalReleaseWindow,
  nowMillis: number
): number {
  const elapsedSeconds = (nowMillis - releaseWindow.occurredAtMillis) / 1000;
  return Math.max(0, Math.floor(releaseWindow.ttlSeconds - elapsedSeconds));
}

/** Uma recusa que ainda se pode liberar, como o botão de liberar precisa dela. */
export interface IReleasableRefusal {
  /** A tentativa recusada; é ela que o botão de liberar manda liberar. */
  attemptId: string;
  /** O equipamento que recusou; liberar pede-o junto com a tentativa. */
  edgeId: string;
  /** A pessoa recusada, para abrir o perfil; `null` quando o evento não a traz. */
  personId: string | null;
  /** A recusa como o equipamento a mandou: pessoa, ponto de acesso, motivo. */
  payload: IEdgeEventPayload;
  /** O prazo do botão de liberar, a correr desde a recusa. */
  releaseWindow: IRefusalReleaseWindow;
}

/** O que a regra lê do evento do hub (`EdgeEventLog`). */
export interface IRefusalEvent {
  edgeId: string;
  personId: string | null;
  /** Já na hora da conta: o hub converte na entrada. */
  occurredOnUtc: string;
  payloadJson: string;
}

/**
 * A recusa, se ela ainda se pode liberar agora — a condição do botão de liberar
 * do item da Live Access Activity: recusa do equipamento, com tentativa,
 * tentativa bloqueada e prazo a correr. O prazo é o mesmo do botão
 * (`getRefusalReleaseWindow` e `getRefusalReleaseRemainingSeconds`): com menos
 * de um segundo o botão já saiu, e nada se anuncia.
 *
 * O item não junta tudo no mesmo sítio: conta o prazo de qualquer recusa
 * (`isDenied`) e exige tentativa e tentativa bloqueada no template do botão.
 * Aqui só interessa o sim ou o não, e vai tudo junto. O som do mini-monitor
 * destacado e a fila de decisões do Controle de acesso no celular decidem por
 * aqui.
 */
export function toReleasableRefusal(
  event: IRefusalEvent,
  nowMillis: number
): IReleasableRefusal | null {
  if (!event.edgeId || !event.payloadJson) return null;

  let payload: IEdgeEventPayload | null;
  try {
    payload = JSON.parse(event.payloadJson) as IEdgeEventPayload;
  } catch {
    return null;
  }
  if (!payload?.AttemptId || !isBlockedAttemptPayload(payload)) return null;

  const releaseWindow = getRefusalReleaseWindow(payload, getDateTime(event.occurredOnUtc));
  if (!releaseWindow) return null;

  return getRefusalReleaseRemainingSeconds(releaseWindow, nowMillis) > 0
    ? {
        attemptId: payload.AttemptId,
        edgeId: event.edgeId,
        personId: event.personId?.trim() || null,
        payload,
        releaseWindow,
      }
    : null;
}
